
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu';
import useRobustTranslation from '@/hooks/useRobustTranslation';

export const NavigationLinks = () => {
  const { getLocalizedPath } = useLanguage();
  const { nav } = useRobustTranslation();
  const location = useLocation();

  const mainNavLinks = [
    { id: 'home', name: nav('home'), path: '/' },
    { id: 'findTherapist', name: nav('findTherapist'), path: '/find' },
    { id: 'specialties', name: nav('specialties'), path: '/specialties' },
    { id: 'howItWorks', name: nav('howItWorks'), path: '/how-it-works' },
    { id: 'forTherapists', name: nav('forTherapists'), path: '/for-therapists' },
    { id: 'pricing', name: nav('pricing'), path: '/pricing' },
  ];

  const aboutLinks = [
    { id: 'aboutUs', name: nav('about'), path: '/about' },
    { id: 'joseLuisMarin', name: 'Dr. José Luis Marín', path: '/jose-luis-marin' },
    { id: 'contact', name: 'Contacto', path: '/contact' },
  ];

  const isActiveLink = (path: string) => {
    return location.pathname === path || location.pathname === getLocalizedPath(path);
  };

  const isAboutSectionActive = () => {
    return aboutLinks.some(link => isActiveLink(link.path));
  };

  return (
    <nav className="flex items-center space-x-2">
      {/* Main Navigation Links */}
      {mainNavLinks.map((link) => (
        <Link
          key={link.id}
          to={getLocalizedPath(link.path)}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap hover:bg-brand-blue-50',
            isActiveLink(link.path)
              ? 'text-brand-blue-700 bg-brand-blue-50'
              : 'text-brand-gray-700 hover:text-brand-blue-600'
          )}
        >
          {link.name}
        </Link>
      ))}
      
      {/* About Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={cn(
            'flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap hover:bg-brand-blue-50',
            isAboutSectionActive()
              ? 'text-brand-blue-700 bg-brand-blue-50'
              : 'text-brand-gray-700 hover:text-brand-blue-600'
          )}>
            {nav('about')}
            <ChevronDown className="ml-1 h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-56 bg-white border border-gray-200 shadow-lg z-[9999] mt-2"
          sideOffset={4}
        >
          <DropdownMenuGroup>
            {aboutLinks.map((link) => (
              <DropdownMenuItem key={link.id} asChild>
                <Link 
                  to={getLocalizedPath(link.path)}
                  className={cn(
                    'w-full cursor-pointer flex items-center px-3 py-2 text-sm rounded-md transition-colors',
                    isActiveLink(link.path)
                      ? 'bg-brand-blue-50 text-brand-blue-600 font-medium' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  )}
                >
                  {link.name}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
};
